import { useEffect } from 'react';
import { ArrowRightIcon, ExternalLinkIcon, PhoneIcon } from 'lucide-react';
import { business, images, routes } from '../data/site';
import { Disclosure } from '../components/Disclosure';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Soak & Share Referral Program | Pegasus Spas Brooklyn, MI';
const DESCRIPTION = 'Soak & Share: refer a friend who buys a new hot tub from Pegasus Spas and earn $100 in-store credit. Five qualifying referrals earn a free Drain, Clean & Refill.';
const CANONICAL = 'https://www.pegasus-spas.com/referral';
const FORM_URL = 'https://forms.gle/5MNm7cGUFtSEB2pu8';
const FORM_SHORT = 'forms.gle/5MNm7cGUFtSEB2pu8';
const rewards = [{
  value: '$100',
  label: 'Pegasus in-store credit',
  body: 'For every friend you refer who buys a NEW hot tub.',
  tone: 'text-pegasus-magenta'
}, {
  value: 'FREE',
  label: 'Drain, Clean & Refill',
  body: 'When you reach five qualifying referrals. Available within 20 miles.',
  tone: 'text-pine'
}, {
  value: 'No limit',
  label: 'Unlimited referrals',
  body: 'Keep sharing. There is no cap on how many friends you refer.',
  tone: 'text-pegasus-blue'
}];
const steps = ['Tell a friend about us and send them the referral form.', 'They fill it out before or at the time of their purchase.', 'Your credit is awarded 30 days after their new hot tub is delivered.'];
const terms = ['The reward applies only when the referred customer purchases a new hot tub.', 'The referral form must be submitted before or at the time of purchase.', 'In-store credit is non-transferable and may be used for products or services at Pegasus Spas.', 'You may not refer yourself or someone in the same household.', 'Credit is awarded 30 days after delivery on a verified sale.', 'Credit expires one year after it is issued.', 'The free drain, clean and refill for five qualifying referrals applies to service within 20 miles.'];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ReferralsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1801" data-mp="jx9rngnxve9x">
      {/* Program opening */}
      <section aria-labelledby="referrals-h1" className="w-full bg-white" data-id="element-1802" data-mp="2gq1us1v0auff">
        <div className="mx-auto max-w-container px-5 pb-10 pt-10 sm:px-8 lg:pb-12 lg:pt-14" data-id="element-1803" data-mp="2gs67n9avv3ru">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-1804" data-mp="2lr8z1qxxxbhc">
            <div className="lg:col-span-6" data-id="element-1805" data-mp="2e6ydfpwomk8z">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1806" data-mp="29f6tvzby1esc">Referral Program</p>
              <h1 id="referrals-h1" className="mt-2 font-display text-[44px] font-semibold leading-[50px] text-ink lg:text-[64px] lg:leading-[70px]" data-id="element-1807" data-mp="24fkhfs3xw2kl">
                Soak &amp; Share
              </h1>
              <p className="mt-4 max-w-xl text-body text-ink/80 sm:text-body-lg" data-id="element-1808" data-mp="2x3znhp53fajs">
                Love your hot tub? Tell your friends. When someone you refer buys a new hot tub from
                us, we&rsquo;ll say thank you with in-store credit.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1809" data-mp="2i676stnt8g40">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer" aria-label="Submit a referral using the Soak and Share form (opens in a new tab)" className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1810" data-mp="1bqwuls3jl54x">
                  Submit a Referral
                  <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1811" data-mp="2qmmzvfabapcx" />
                </a>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1812" data-mp="3ed0k4jmnnjwy">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1813" data-mp="39cuxjhv8snlq" />
                  Questions? {business.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-1814" data-mp="1xyhaugc86e7a">
              <figure data-id="element-1815" data-mp="1xzabczklc7gz">
                <div className="aspect-[3/2] w-full overflow-hidden rounded-card bg-water" data-id="element-1816" data-mp="1xwee7dgkhmtl">
                  <img src={images.sundanceCouple} alt="A couple relaxing together in a hot tub" className="h-full w-full object-cover object-center" decoding="async" data-id="element-1817" data-mp="fz1aqv76f3zw" />
                </div>
                <figcaption className="mt-2 text-[14px] leading-5 text-ink/60" data-id="element-1818" data-mp="l0gw6od9ncit">
                  Sundance Spas lifestyle photography
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section aria-labelledby="rewards-heading" className="w-full bg-water" data-id="element-1819" data-mp="62y7tn4i31bw">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1820" data-mp="1c84sgjjrakd">
          <h2 id="rewards-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1821" data-mp="1a54n3ook11x">
            What You Earn
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-1822" data-mp="35o3hw8hisp2k">
            {rewards.map(reward => <li key={reward.label} className="flex flex-col rounded-card bg-white px-6 py-7" data-id="element-1823" data-mp="1don2potzlsk4">
                <p className={`font-display text-[46px] font-semibold leading-none sm:text-[52px] ${reward.tone}`} data-id="element-1824" data-mp="1dmaewv1jsnz5">
                  {reward.value}
                </p>
                <h3 className="mt-4 text-[20px] font-semibold leading-7 text-ink" data-id="element-1825" data-mp="pqpiiuamsjvg">
                  {reward.label}
                </h3>
                <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-1826" data-mp="uqu06nn9zkn9">{reward.body}</p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* How it works + terms */}
      <section aria-labelledby="how-heading" className="w-full bg-white" data-id="element-1827" data-mp="14ozzcxhp6cof">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1828" data-mp="2b4amuv7f1a2t">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1829" data-mp="2b6dgrqbovnct">
            <div className="lg:col-span-6" data-id="element-1830" data-mp="2g1an2phl3im0">
              <h2 id="how-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1831" data-mp="4g9zwd91n96b">
                How It Works
              </h2>
              <ol className="mt-6 border-t border-ink/15" data-id="element-1832" data-mp="3qul4ws13u16c">
                {steps.map((step, index) => <li key={step} className="flex gap-5 border-b border-ink/15 py-4" data-id="element-1833" data-mp="3lv8iu9i24y4t" data-mpi={index}>
                    <span aria-hidden="true" className="font-display text-[26px] font-semibold leading-7 text-pegasus-magenta" data-id="element-1834" data-mp="3gxf9x8pddq0u">
                      {index + 1}
                    </span>
                    <p className="text-[17px] leading-7 text-ink/85" data-id="element-1835" data-mp="37yl9f78x4fi0">{step}</p>
                  </li>)}
              </ol>

              <a href={FORM_URL} target="_blank" rel="noopener noreferrer" aria-label="Open the Soak and Share referral form (opens in a new tab)" className="mt-7 inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1836" data-mp="165nhreqvnsyn">
                Open the Soak &amp; Share Form
                <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1837" data-mp="59otxa6jrnfq" />
              </a>
              <p className="mt-3 break-words text-[15.5px] leading-6 text-ink/65" data-id="element-1838" data-mp="3nfuvdicluu3g">{FORM_SHORT}</p>
            </div>

            <div id="referral-terms" className="scroll-mt-28 lg:col-span-6 lg:pt-2" data-id="element-1839" data-mp="3sf7kk9qy8d97">
              <Disclosure label="Program terms" data-id="element-1840" data-mp="3dhnibalfsw6q">
                <ul className="space-y-2.5" data-id="element-1841" data-mp="3diylb8725zws">
                  {terms.map(term => <li key={term} className="text-[16px] leading-6 text-ink/80" data-id="element-1842" data-mp="3ih9v5ixcsaf0">
                      {term}
                    </li>)}
                </ul>
                <p className="mt-4 text-[16px] leading-6 text-ink/75" data-id="element-1843" data-mp="1gpdk0af3l2kr">
                  Not sure whether a referral qualifies? Call or text us at{' '}
                  <a href={business.phoneHref} className="font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta" data-id="element-1844" data-mp="3153bu7xx7v2y">
                    {business.phone}
                  </a>{' '}
                  before the purchase.
                </p>
              </Disclosure>

              <p className="mt-8 text-[16.5px] leading-7 text-ink/75" data-id="element-1845" data-mp="3fw2ez3fzlrf5">
                Referrals and repeat customers have been part of how we&rsquo;ve grown since 1977.
                Soak &amp; Share is our way of saying thanks.
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-8" data-id="element-1846" data-mp="3oy1ufrbcsnfz">
                {[{
                label: 'Read Customer Reviews',
                href: routes.reviews
              }, {
                label: 'Why Pegasus',
                href: routes.about
              }].map(link => <li key={link.label} data-id="element-1847" data-mp="2j23gzfoqz2n8">
                    <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1848" data-mp="2j364ddmv2mye">
                      {link.label}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1849" data-mp="31scrrdylo2qj" />
                    </SiteLink>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>;
}